import { useCallback, useId, useMemo, useState } from 'react';

import { EntityBadgeById } from '@/lib/crm/components/badges';
import { useAssociations } from '@/lib/crm/hooks/useAssociations';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import type { AssociationDetail, CrmObject } from '@/types/crm';

import type {
  AssociationType,
  CrmObjectTypeSchema,
} from '../../core/SchemaClient';
import { formatFallback } from '../../utils/labelFormatters';
import { getEntityTypeConfig } from '../registry/entityTypeRegistry';
import { HIDDEN_ASSOCIATION_TARGETS } from '../registry/fieldConstants';
import {
  isDecomposableRule,
  planAssociationFields,
} from '../utils/buildDetailFieldList';

const VISIBLE_LIMIT = 10;

/** Stable empty fallback so a loading object-types query doesn't churn memos. */
const EMPTY_TYPES: CrmObjectTypeSchema[] = [];

/** Order a group's entries by their target's display name. */
function sortByDisplayName(
  details: readonly AssociationDetail[],
  hydrated: Record<string, CrmObject>
): AssociationDetail[] {
  return [...details].sort((a, b) => {
    const objA = hydrated[a.target_entity_id];
    const objB = hydrated[b.target_entity_id];
    const nameA = objA
      ? getEntityTypeConfig(objA.entity_type_name).getDisplayName(objA)
      : a.target_entity_id;
    const nameB = objB
      ? getEntityTypeConfig(objB.entity_type_name).getDisplayName(objB)
      : b.target_entity_id;
    return nameA.localeCompare(nameB);
  });
}

export interface AssociationsTabProps {
  object: CrmObject;
}

interface AssociationGroup {
  /**
   * Identity of the rendered group — the association type name, or
   * `${type}::${targetType}` when a kind-based rule is split into one group per
   * resolved target type. Opaque: used for the React key, the expand set, and
   * the toggle's `data-type`.
   */
  groupKey: string;
  /** The underlying association type name (shared by a rule's subsets). */
  associationType: string;
  label: string;
  entries: AssociationDetail[];
}

/**
 * AssociationsTab — groups every association on the current record by
 * association-type and renders each group as a tight card.
 *
 * Label resolution: use the schema-defined `label` from
 * `useAssociationTypes` (e.g. "Lifecycle Stage", "Works at Company")
 * rather than the target entity's plural display name. The previous
 * implementation labeled by target plural (e.g. "Records" for both
 * lifecycle stages AND enrichment requests), producing visually
 * duplicate section headers that hid the actual relationship type.
 *
 * Visual language matches `HighlightCardShell` for consistency on the
 * full-page record view: white `bg-primary`, `border-secondary`,
 * `shadow-xs`, `rounded-xl`, `px-3.5 py-3`, subdued `text-xs` label.
 */
export function AssociationsTab({ object }: AssociationsTabProps) {
  const { associationTypes, associationTypeMap } = useAssociationTypes(
    object.org_id
  );

  // Resolved association targets, so a kind-based rule can be grouped by the
  // type each target actually is. Reads the store first and batch-fetches only
  // what is missing — the same path the record's overview uses.
  const { associations: resolvedAssociations } = useAssociations(
    object.org_id,
    object.associations ?? [],
    { sourceEntityTypeName: object.entity_type_name }
  );
  const resolvedTypeByTargetId = useMemo(() => {
    const map = new Map<string, string>();
    for (const assoc of resolvedAssociations) {
      if (assoc.object) {
        map.set(assoc.object.entity_id, assoc.object.entity_type_name);
      }
    }
    // `associated_objects` is already on the record for the full-page view;
    // fold it in so a target the store has not resolved yet is still typed.
    for (const [entityId, obj] of Object.entries(
      object.associated_objects ?? {}
    )) {
      if (obj?.entity_type_name && !map.has(entityId)) {
        map.set(entityId, obj.entity_type_name);
      }
    }
    return map;
  }, [resolvedAssociations, object.associated_objects]);

  const assocTypeByName = useMemo(() => {
    const map = new Map<string, AssociationType>();
    for (const at of associationTypes) {
      map.set(at.association_type_name, at);
    }
    return map;
  }, [associationTypes]);

  const objectTypesQuery = useObjectTypes(object.org_id);
  const allObjectTypes = objectTypesQuery.data?.allObjectTypes ?? EMPTY_TYPES;
  /** entity_type_name -> its plural label, for naming a decomposed group. */
  const typeLabelPluralMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of allObjectTypes) {
      map.set(t.entity_type_name, t.label_plural || t.label);
    }
    return map;
  }, [allObjectTypes]);

  // Build a name → human label map from the schema using the actual
  // `label` field. The previous implementation iterated
  // `associationTypeMap` (name → target entity type) and re-derived the
  // label from the name via `formatFallback`, which discarded any
  // backend-customized labels (e.g. "Works at Company") and produced a
  // tokenized version of the type-name instead.
  const labelByType = useMemo(() => {
    const map = new Map<string, string>();
    for (const assoc of associationTypes) {
      if (assoc.label) {
        map.set(assoc.association_type_name, assoc.label);
      }
    }
    return map;
  }, [associationTypes]);

  const groups = useMemo<AssociationGroup[]>(() => {
    const associations = object.associations ?? [];
    const hydrated = object.associated_objects ?? {};

    const map = new Map<string, AssociationDetail[]>();
    const groupMeta = new Map<
      string,
      { associationType: string; targetEntityTypeName?: string }
    >();
    for (const detail of associations) {
      const targetType = associationTypeMap.get(detail.association_type_name);
      // WORKAROUND until #12411: see fieldConstants.ts HIDDEN_ASSOCIATION_TARGETS
      if (targetType && HIDDEN_ASSOCIATION_TARGETS.has(targetType)) {
        continue;
      }

      // A kind-based rule carries one label for every type it resolves to, so
      // grouping by rule alone puts a contact's tasks under "Associated Deals".
      // Group by the type each target ACTUALLY resolved to instead, and drop
      // the types a dedicated section owns.
      // A rule resolving to a single type is NOT decomposed — it keeps its own
      // label, exactly as the overview does. Both surfaces ask
      // `planAssociationFields`, so they cannot disagree about a given rule.
      const assocType = assocTypeByName.get(detail.association_type_name);
      const plan = isDecomposableRule(assocType)
        ? planAssociationFields(assocType, allObjectTypes)
        : undefined;
      // `none` — every resolvable type is section-owned, so the rule
      // contributes no detail here at all, matching
      // `expandKindBasedAssociationFields`.
      if (plan?.kind === 'none') {
        continue;
      }

      const resolvedType = resolvedTypeByTargetId.get(detail.target_entity_id);
      const decomposed = plan?.kind === 'split' && !!resolvedType;
      // WORKAROUND until #12411: see fieldConstants.ts HIDDEN_ASSOCIATION_TARGETS
      if (decomposed && HIDDEN_ASSOCIATION_TARGETS.has(resolvedType)) {
        continue;
      }

      const groupKey = decomposed
        ? `${detail.association_type_name}::${resolvedType}`
        : detail.association_type_name;
      let bucket = map.get(groupKey);
      if (!bucket) {
        bucket = [];
        map.set(groupKey, bucket);
        groupMeta.set(groupKey, {
          associationType: detail.association_type_name,
          targetEntityTypeName: decomposed ? resolvedType : undefined,
        });
      }
      bucket.push(detail);
    }

    const result: AssociationGroup[] = [];
    for (const [groupKey, details] of map.entries()) {
      const meta = groupMeta.get(groupKey);
      const associationType = meta?.associationType ?? groupKey;
      // A decomposed group is named for the records it holds ("Deals").
      if (meta?.targetEntityTypeName) {
        const plural =
          typeLabelPluralMap.get(meta.targetEntityTypeName) ??
          getEntityTypeConfig(meta.targetEntityTypeName).pluralDisplayName;
        result.push({
          groupKey,
          associationType,
          label: plural,
          entries: sortByDisplayName(details, hydrated),
        });
        continue;
      }
      // Prefer schema label → formatted type name → target's plural name.
      // The first two are stable across records and disambiguate
      // distinct association types that happen to share a target entity
      // type (e.g. "Created by user" and "Owner" both point to user).
      const schemaLabel = labelByType.get(associationType);
      let label = schemaLabel ?? formatFallback(associationType);
      if (!schemaLabel) {
        for (const detail of details) {
          const obj = hydrated[detail.target_entity_id];
          if (obj) {
            label = getEntityTypeConfig(obj.entity_type_name).pluralDisplayName;
            break;
          }
        }
      }

      result.push({
        groupKey,
        associationType,
        label,
        entries: sortByDisplayName(details, hydrated),
      });
    }

    result.sort((a, b) => a.label.localeCompare(b.label));
    return result;
  }, [
    object.associations,
    object.associated_objects,
    labelByType,
    associationTypeMap,
    assocTypeByName,
    resolvedTypeByTargetId,
    typeLabelPluralMap,
    allObjectTypes,
  ]);

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const panelIdBase = useId();

  const toggleExpanded = useCallback((type: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const handleToggleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const { type } = e.currentTarget.dataset;
      if (type) {
        toggleExpanded(type);
      }
    },
    [toggleExpanded]
  );

  if (!object.associations || object.associations.length === 0) {
    // Tight empty state — matches `HighlightCardShell` proportions
    // instead of the previous ~150px centered block.
    return (
      <div className='bg-primary border border-secondary rounded-xl px-3.5 py-3 shadow-xs'>
        <p className='text-xs font-medium text-[var(--color-text-secondary)] mb-1'>
          Associations
        </p>
        <p className='text-md font-medium text-[var(--color-text-tertiary)]'>
          No related records yet
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      {groups.map(({ groupKey, label, entries }, groupIndex) => {
        const isExpanded = expanded.has(groupKey);
        const showAll = isExpanded || entries.length <= VISIBLE_LIMIT;
        const visibleEntries = showAll
          ? entries
          : entries.slice(0, VISIBLE_LIMIT);
        const hiddenCount = entries.length - VISIBLE_LIMIT;
        const panelId = `${panelIdBase}-panel-${groupIndex}`;

        return (
          <section
            key={groupKey}
            aria-label={`${label} associations`}
            aria-live='polite'
            className='bg-primary border border-secondary rounded-xl px-3.5 py-3 shadow-xs'
          >
            <div className='flex items-center gap-2 mb-2'>
              <p className='text-xs font-medium text-[var(--color-text-secondary)] truncate'>
                {label}
              </p>
              <span
                className='inline-flex items-center justify-center min-w-[18px] px-1.5 h-[18px] rounded-full bg-[var(--color-bg-secondary)] text-[10px] font-medium text-[var(--color-text-tertiary)]'
                aria-label={`${entries.length} ${entries.length === 1 ? 'record' : 'records'}`}
              >
                {entries.length}
              </span>
            </div>
            <div id={panelId} className='flex flex-wrap gap-1.5'>
              {visibleEntries.map(detail => (
                <EntityBadgeById
                  key={detail.target_entity_id}
                  entityId={detail.target_entity_id}
                  orgId={object.org_id}
                  variant='default'
                  fallbackEntityType={
                    object.associated_objects?.[detail.target_entity_id]
                      ?.entity_type_name
                  }
                />
              ))}
            </div>
            {entries.length > VISIBLE_LIMIT && (
              <button
                type='button'
                data-type={groupKey}
                onClick={handleToggleClick}
                aria-expanded={isExpanded}
                aria-controls={panelId}
                aria-label={
                  isExpanded
                    ? `Show fewer ${label}`
                    : `Show ${hiddenCount} more ${label}`
                }
                className='mt-2 text-xs font-medium text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] transition-colors'
              >
                {isExpanded ? 'Show less' : `+${hiddenCount} more`}
              </button>
            )}
          </section>
        );
      })}
    </div>
  );
}
